import React, { useState } from 'react';
import { useAppStore } from '../../store';
import apiService from '../../services/api';

interface AuthModalProps {
  show: boolean;
  mode: 'login' | 'register';
  onHide: () => void;
  onSwitchMode: (mode: 'login' | 'register') => void;
}

const AuthModal: React.FC<AuthModalProps> = ({ show, mode, onHide, onSwitchMode }) => {
  const { login, register } = useAppStore();
  const [formData, setFormData] = useState({
    account: '',
    phone: '',
    username: '',
    password: '',
    qianwen_api_key: '',
    github_token: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [agreedToTerms, setAgreedToTerms] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      let success = false;

      if (mode === 'login') {
        // 使用账号和密码登录
        success = await login(formData.account, formData.password);
      } else {
        if (!agreedToTerms) {
          setError('请先阅读并同意服务声明和隐私政策后再注册');
          setLoading(false);
          return;
        }
        // 注册时需要用户名、手机号、密码
        success = await register(
          formData.phone,
          formData.username,
          formData.password,
          formData.qianwen_api_key,
          formData.github_token
        );
      }

      if (success) {
        onHide();
        setFormData({
          account: '',
          phone: '',
          username: '',
          password: '',
          qianwen_api_key: '',
          github_token: ''
        });
        setAgreedToTerms(false);
      }
    } catch (error: any) {
      // 处理特定的错误信息
      let errorMessage = '操作失败，请稍后重试';

      if (error.response?.data?.error) {
        errorMessage = error.response.data.error;

        // 如果是手机号已注册的错误，提供更友好的提示
        if (errorMessage === '该手机号已注册') {
          errorMessage = '该手机号已注册，请直接登录或使用其他手机号注册';
        }
      } else if (error.message) {
        errorMessage = error.message;
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleOverlayClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onHide();
    }
  };

  if (!show) return null;

  return (
    <div className={`auth-overlay ${show ? 'show' : ''}`} onClick={handleOverlayClick}>
      <div className="auth-modal" onClick={(e) => e.stopPropagation()}>
        <div className="auth-header">
          <h3>
            <i className={`fas ${mode === 'login' ? 'fa-sign-in-alt' : 'fa-user-plus'}`}></i>
            {mode === 'login' ? '用户登录' : '用户注册'}
          </h3>
          <button className="auth-close" onClick={onHide} title="关闭">
            <i className="fas fa-times"></i>
          </button>
        </div>

        <div className="auth-content">
          {error && (
            <div className="alert alert-danger" role="alert">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {mode === 'login' ? (
              // 登录表单
              <>
                <div className="form-group">
                  <label htmlFor="account">
                    <i className="fas fa-user"></i> 用户名 / 手机号
                  </label>
                  <input
                    type="text"
                    id="account"
                    name="account"
                    value={formData.account}
                    onChange={handleInputChange}
                    placeholder="请输入用户名或手机号"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="password">
                    <i className="fas fa-lock"></i> 密码
                  </label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="请输入密码"
                    required
                  />
                </div>

                <button type="submit" className="auth-submit-btn" disabled={loading}>
                  <i className="fas fa-sign-in-alt"></i>
                  {loading ? '登录中...' : '登录'}
                </button>

                <div className="auth-switch">
                  还没有账号？<a href="#" onClick={(e) => { e.preventDefault(); onSwitchMode('register'); }}>立即注册</a>
                </div>
              </>
            ) : (
              // 注册表单
              <>
                <div className="form-group">
                  <label htmlFor="username">
                    <i className="fas fa-user"></i> 用户名
                  </label>
                  <input
                    type="text"
                    id="username"
                    name="username"
                    value={formData.username}
                    onChange={handleInputChange}
                    placeholder="请输入用户名（1-20个字符）"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="phone">
                    <i className="fas fa-phone"></i> 手机号
                  </label>
                  <input
                    type="tel"
                    id="phone"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    placeholder="请输入手机号"
                    required
                  />
                </div>

                <div className="form-group">
                  <label htmlFor="password">
                    <i className="fas fa-lock"></i> 密码
                  </label>
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleInputChange}
                    placeholder="请输入密码（至少6位，包含字母和数字）"
                    required
                  />
                </div>

                {/* API配置 */}
                <div className="api-config-section">
                  <h4>
                    <i className="fas fa-cog"></i> API 配置（可选）
                  </h4>
                  <p className="api-config-desc">配置 API 密钥以解锁全部功能。</p>

                  <div className="form-group">
                    <label htmlFor="qianwen_api_key">
                      <i className="fas fa-brain"></i> LLM 模型 API Key
                    </label>
                    <input
                      type="text"
                      id="qianwen_api_key"
                      name="qianwen_api_key"
                      value={formData.qianwen_api_key}
                      onChange={handleInputChange}
                      placeholder="用于 AI 项目解读功能"
                    />
                    <small className="form-text">
                      用于 AI 功能，获取方式：
                      <a href="https://modelscope.cn/my/myaccesstoken" target="_blank" rel="noopener noreferrer">
                        魔搭 ModelScope
                      </a>
                    </small>
                  </div>

                  <div className="form-group">
                    <label htmlFor="github_token">
                      <i className="fab fa-github"></i> GitHub Token
                    </label>
                    <input
                      type="text"
                      id="github_token"
                      name="github_token"
                      value={formData.github_token}
                      onChange={handleInputChange}
                      placeholder="用于获取更多项目信息"
                    />
                    <small className="form-text">
                      用于提高 API 限制，获取方式：
                      <a href="https://github.com/settings/tokens" target="_blank" rel="noopener noreferrer">
                        GitHub Settings → Developer settings → Personal access tokens
                      </a>
                    </small>
                  </div>
                </div>

                <div className="form-group auth-terms-check">
                  <label>
                    <input
                      type="checkbox"
                      checked={agreedToTerms}
                      onChange={(e) => setAgreedToTerms(e.target.checked)}
                    />
                    <span className="ms-2">
                      我已阅读并同意
                      <a href="/legal/service" target="_blank" rel="noopener noreferrer">《服务声明》</a>
                      和
                      <a href="/legal/privacy" target="_blank" rel="noopener noreferrer">《隐私政策》</a>
                    </span>
                  </label>
                </div>

                <button type="submit" className="auth-submit-btn" disabled={loading}>
                  <i className="fas fa-user-plus"></i>
                  {loading ? '注册中...' : '注册'}
                </button>

                <div className="auth-switch">
                  已有账号？<a href="#" onClick={(e) => { e.preventDefault(); onSwitchMode('login'); }}>立即登录</a>
                </div>

                <div className="auth-tips">
                  <small className="text-muted">
                    <i className="fas fa-info-circle"></i>
                    如果手机号已注册，请直接登录
                  </small>
                </div>
              </>
            )}
          </form>
        </div>
      </div>
    </div>
  );
};

export default AuthModal;

